import * as S from './Preparation.styles'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'

import FoundationIcon from '@ovaeasy/react-native-vector-icons/Foundation'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import { TeamActionMenu } from './TeamActionMenu'
import { TeamRole } from '@app/evaluation-core/types'
import { setUserColor } from '@app/evaluation-core/actions/evaluationActions'
import theme from '@app/ui/theme'
import { useEvaluation } from '@common/hooks'
import useInvite from '@common/hooks/useInvite'
import useUser from '@common/hooks/useUser'
import useReadOnly from '../hooks/useReadOnly'
import useEvaluationUser from '@common/hooks/useEvaluationUser'
import { EvalStatus } from '@app/courses/domain/ratingStatus'
import SearchInvitableRatersModal from './SearchInvitableRaterModal'
import { useSnackbar } from '@app/snackbar'

const validateEmail = (value: string) =>
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
    value
  )

const MEMBER_STATUS = ['Pending Invitation', 'Active', 'Declined', 'Expired']
const MEMBER_ROLE = ['Leader', 'Member']

export const PreparationTeam: React.FC = () => {
  const { currentId, evaluation } = useEvaluation()
  const evalUser = useEvaluationUser()
  const user = useUser()
  const [email, setEmail] = useState('')
  const userId = useUser()?.id
  const [color, setColor] = useState(theme.colors.primary)
  const [changingId, setChangingId] = useState('')
  const handleReadOnly = useReadOnly()
  const [loadingInvite, setLoadingInvite] = useState(false)
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const canEdit = evaluation.status !== EvalStatus.FINALIZED
  const [openSnackbar] = useSnackbar()

  const dispatch = useDispatch<any>()
  const { sendInvite } = useInvite()

  if (!evaluation || !userId || !currentId) return null

  async function sendInvitation() {
    setLoadingInvite(true)
    await sendInvite(email, currentId, () => setEmail('')).finally(() =>
      setLoadingInvite(false)
    )
  }

  const handleChangeComplete = (color: any, userId?: string) => {
    setColor(color.hex)
    dispatch(setUserColor(color.hex, userId ?? changingId))
  }

  const tableRowWidths = '35% 20% 20% 5% 20%'

  const sortedUsers = Object.entries(evaluation.users)
    .sort(([userIdA], [userIdB]) => {
      if (+userIdA === +userIdB) {
        return 0
      }
      return +userIdA < +userIdB ? -1 : 1
    })
    // The key is what every write targets, so that is the id we trust. Rows
    // missing the userId field can then be acted on like any other.
    .map(([userId, user]) => ({ ...user, userId }))
    .filter((user) => user.role !== TeamRole.READ_ONLY)
  //TODO: This prohibits users to see themself in new values. But maybe there is a thinking to this?
  // .filter(
  //   (listUser) => user.isAdmin || listUser.role !== TeamRole.SYSTEM_ADMIN
  // )

  return (
    <>
      <S.ContentCard>
        <S.ContentHeading>Team</S.ContentHeading>
        <S.ContentSeparator />

        <S.TeamTable>
          <S.TeamTableRow height="40px" width={tableRowWidths}>
            <S.TeamTableColTop>Email</S.TeamTableColTop>
            <S.TeamTableColTop>Role</S.TeamTableColTop>
            <S.TeamTableColTop>Color</S.TeamTableColTop>
            <S.TeamTableColTop></S.TeamTableColTop>
            <S.TeamTableColTop></S.TeamTableColTop>
          </S.TeamTableRow>
          {sortedUsers.map((user, index: number) => {
            const defaultColor = user?.view?.color ?? theme.colors.primary
            const isOwnUser = userId == +user.userId

            return (
              <S.TeamTableRow height="60px" width={tableRowWidths} key={index}>
                <S.TeamTableCol isOwnUser={isOwnUser}>
                  {user?.view?.email}
                </S.TeamTableCol>

                <S.TeamTableCol>
                  {user.role === TeamRole.LEADER && (
                    <>
                      <FoundationIcon
                        name="crown"
                        color={defaultColor}
                        size={50}
                      />
                    </>
                  )}
                  {user.role === TeamRole.SYSTEM_ADMIN && (
                    <div style={{ width: 50, textAlign: 'left' }}>
                      <span style={{ fontWeight: 'bold', fontSize: 10 }}>
                        ADMIN
                      </span>
                    </div>
                  )}
                </S.TeamTableCol>
                <S.TeamTableCol>
                  <div
                    style={{
                      width: 25,
                      height: 25,
                      background: defaultColor,
                      borderRadius: 500,
                    }}
                    onClick={() => {
                      dispatch({
                        type: 'SET_MODAL',
                        params: {
                          mode: 'colorOverlay',
                          defaultColor,
                          userId: user.userId,
                          handleChange: handleChangeComplete,
                        },
                      })
                      if (user.userId) {
                        setChangingId(user.userId)
                      }
                    }}
                  />
                </S.TeamTableCol>
                <S.TeamTableCol></S.TeamTableCol>
                <S.TeamTableCol style={{ zIndex: 13000 - index }}>
                  <TeamActionMenu userId={user.userId} />
                </S.TeamTableCol>
              </S.TeamTableRow>
            )
          })}
        </S.TeamTable>
        {(evalUser.isAdmin || user.isAdmin) && (
          <S.TeamInvitationArea>
            {/* <input */}
            {/*   type="text" */}
            {/*   value={email} */}
            {/*   placeholder="Enter E-mail/Golf-ID" */}
            {/*   autoCapitalize="off" */}
            {/*   onChange={(event) => setEmail(event.target.value)} */}
            {/*   disabled={!canEdit} */}
            {/* /> */}
            <div
              onClick={() => {
                if (!canEdit) {
                  openSnackbar(
                    'This rating is finalized. To invite a new team member, create a new revision instead.'
                  )
                }
              }}
            >
              <RoundedButton
                loading={loadingInvite}
                onPress={() => setInviteModalOpen(true)}
                title={'Invite Team Member'}
                width={200}
                disabled={!canEdit}
              />
            </div>
            <SearchInvitableRatersModal
              isOpen={inviteModalOpen}
              onClose={() => setInviteModalOpen(false)}
            />
          </S.TeamInvitationArea>
        )}
      </S.ContentCard>
    </>
  )
}
